import { useState, useEffect, useRef } from "react";
import axios from "axios";
import DashNavbar from "./Misc/DashNavbar";
import Sidebar from "./Misc/Sidebar";
import { GoArrowRight } from "react-icons/go";
import personality13 from "../Assets/personality13.png";
import "../index.css";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";

const PersonalityQuizForm = () => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [textAnswers, setTextAnswers] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizTaken, setQuizTaken] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [applicantId, setApplicantId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedQuestions, setTouchedQuestions] = useState({}); // Track if question has been interacted with

  const navigate = useNavigate();
  const location = useLocation();

  const quizType = localStorage.getItem("quiz_type");
  const jobId = localStorage.getItem("job_id");
  const postingType = localStorage.getItem("selected_posting_type") || "Job_Opening";
  const returnPath = location.state?.from || "/job-openings";

  useEffect(() => {
    const user_id_from_storage = localStorage.getItem("user_id");

    if (!jobId) {
      Swal.fire({
        icon: "warning",
        title: "No Job Selected",
        text: "Please select a job before taking the quiz.",
        confirmButtonText: "Go Back to Jobs",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/job-openings");
        }
      });
      return;
    }

    const checkQuizStatus = async () => {
      try {
        const response = await axios.get(
          `https://raasbackend.vetrinahealthcare.com/check_quiz_status/${user_id_from_storage}/`
        );
        if ("quiz_taken" in response.data) {
          setQuizTaken(response.data.quiz_taken);
        }
      } catch (error) {
        console.error(error.message);
      }
    };

    const ensureApplicantExists = async () => {
      try {
        const resp = await axios.get(
          `https://raasbackend.vetrinahealthcare.com/applicants/data/?user_id=${user_id_from_storage}`
        );
        const data = resp?.data;
        const first = Array.isArray(data) ? data[0] : data;
        if (!first) {
          await Swal.fire({
            icon: "warning",
            title: "Complete Your Profile",
            text: "Please complete your profile before taking the quiz.",
            confirmButtonText: "Go to My Profile",
          });
          navigate("/jobapplication");
          return false;
        }
        const extractedId = first.id || first.applicant || first.pk || (first.fields && (first.fields.id || first.fields.applicant));
        if (extractedId) {
          setApplicantId(extractedId);
          localStorage.setItem("applicant_id", String(extractedId));
        }
        return true;
      } catch (e) {
        return true;
      }
    };

    const fetchQuestions = async () => {
      setIsLoadingQuestions(true);
      try {
        let response;
        try {
          response = await axios.get(`https://raasbackend.vetrinahealthcare.com/questions/${postingType}/${jobId}/`);
        } catch (e) {
          response = await axios.get(`https://raasbackend.vetrinahealthcare.com/questions/${jobId}/`);
        }
        const data = response?.data;
        const normalized = Array.isArray(data) ? data : (data?.questions || data?.results || []);
        setQuestions(normalized);
        setAnswers(Array(normalized.length).fill(null));
        setTextAnswers(Array(normalized.length).fill(""));
        // Initialize validation errors and touched state
        const errors = {};
        const touched = {};
        normalized.forEach((_, index) => {
          errors[index] = false;
          touched[index] = false;
        });
        setValidationErrors(errors);
        setTouchedQuestions(touched);
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Could not load questions." });
      } finally {
        setIsLoadingQuestions(false);
      }
    };

    const init = async () => {
      if (user_id_from_storage) checkQuizStatus();
      if (await ensureApplicantExists()) fetchQuestions();
    };
    init();
  }, [jobId, navigate, postingType]);

  const handleStartQuiz = () => {
    if (quizTaken) {
      Swal.fire({ icon: "info", title: "Already Taken", text: "Quiz already completed." });
    } else {
      setQuizStarted(true);
    }
  };

  const handleResponseChange = (index, answerIndex) => {
    // Mark question as touched
    setTouchedQuestions(prev => ({
      ...prev,
      [index]: true
    }));
    
    setAnswers((prev) => {
      const updated = [...prev];
      updated[index] = answerIndex;
      return updated;
    });
    
    // Clear validation error when answer is selected
    setValidationErrors(prev => ({
      ...prev,
      [index]: false
    }));
  };

  const handleTextAnswerChange = (index, text) => {
    // Mark question as touched
    setTouchedQuestions(prev => ({
      ...prev,
      [index]: true
    }));
    
    setTextAnswers((prev) => {
      const updated = [...prev];
      updated[index] = text;
      return updated;
    });
    
    // Clear validation error when text is entered
    if (text.trim() !== "") {
      setValidationErrors(prev => ({
        ...prev,
        [index]: false
      }));
    }
  };

  
  // Helper functions to distinguish MCQ options vs Text/Descriptive questions
  const isQuestionMCQ = (question) => {
    if (!question) return false;
    const hasMultipleAnswers = Array.isArray(question.answers) && question.answers.length > 1;
    const hasValidOptionText = hasMultipleAnswers && question.answers.some(a => a && String(a).trim() !== "");
    const isDescriptiveType = question.question_type === "descriptive" || question.question_type === "text" || question.type === "descriptive" || question.type === "text";
    return hasValidOptionText && !isDescriptiveType;
  };

  const isQuestionTextAnswer = (question) => {
    if (!question) return true;
    return !isQuestionMCQ(question);
  };

  // Function to validate all questions are answered
  const validateAllQuestions = () => {
    const errors = {};
    let hasError = false;

    questions.forEach((question, index) => {
      const isMCQ = isQuestionMCQ(question);
      const isTextAnswer = isQuestionTextAnswer(question);
      
      if (isMCQ && answers[index] === null) {
        errors[index] = true;
        hasError = true;
      } else if (isTextAnswer && (!textAnswers[index] || textAnswers[index].trim() === "")) {
        errors[index] = true;
        hasError = true;
      } else {
        errors[index] = false;
      }
    });

    setValidationErrors(errors);
    return !hasError;
  };

  // Function to scroll to first unanswered question
  const scrollToFirstUnansweredQuestion = () => {
    const firstErrorIndex = Object.keys(validationErrors).find(key => validationErrors[key] === true);
    if (firstErrorIndex !== undefined) {
      const element = document.getElementById(`question-${firstErrorIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add highlight effect
        element.classList.add('highlight-error');
        setTimeout(() => {
          element.classList.remove('highlight-error');
        }, 2000);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate all questions are answered
    if (!validateAllQuestions()) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Quiz",
        html: `
          <div style="text-align: left;">
            <p>Please answer all questions before submitting.</p>
            <p><strong>Unanswered Questions:</strong></p>
            <ul>
              ${Object.entries(validationErrors)
                .filter(([_, hasError]) => hasError)
                .map(([index]) => `<li>Question ${parseInt(index) + 1}</li>`)
                .join('')}
            </ul>
          </div>
        `,
        confirmButtonText: "Go to First Unanswered",
      }).then((result) => {
        if (result.isConfirmed) {
          scrollToFirstUnansweredQuestion();
        }
      });
      return;
    }

    setIsLoading(true);

    try {
      const user_id_from_storage = localStorage.getItem("user_id");
      const idForSubmissionRaw = applicantId || localStorage.getItem("applicant_id") || user_id_from_storage;
      const idForSubmission = parseInt(idForSubmissionRaw, 10);

      // UPDATED: New payload structure
      const selected_answers = {};
      questions.forEach((question, idx) => {
        const userSelectedIndex = answers[idx];
        const questionId = idx + 1; // Assuming question IDs start from 1
        
        if (isQuestionMCQ(question)) {
          // MCQ question
          if (userSelectedIndex !== null) {
            const isCorrect = userSelectedIndex === question.correct_answer_index ? "correct" : "wrong";
            selected_answers[questionId] = {
              selected_index: userSelectedIndex,
              is_correct: isCorrect
            };
          }
        } else if (isQuestionTextAnswer(question)) {
          // Text answer question
          const textAnswer = textAnswers[idx];
          if (textAnswer && textAnswer.trim() !== "") {
            // For text answers, we always mark as "correct" since we can't validate automatically
            selected_answers[questionId] = {
              selected_index: null, // No index for text answers
              text_answer: textAnswer.trim(),
              is_correct: "correct" // Always marked as correct
            };
          }
        }
      });

      const payload = {
        applicant_id: idForSubmission,
        job_id: jobId,
        quiz_type: quizType,
        selected_answers: selected_answers
      };

      console.log("Submitting payload:", JSON.stringify(payload, null, 2)); // For debugging

      const csrfToken = getCookie("csrftoken");
      const response = await axios.post(
        "https://raasbackend.vetrinahealthcare.com/save_quiz_answers/",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );

      if (response.data.success) {
        await Swal.fire({
          icon: "success",
          title: "Quiz Submitted!",
          text: `Score: ${response.data.data.status}%`,
          timer: 3000,
          showConfirmButton: false,
        });

        try {
          const selectedJobTitle = localStorage.getItem("selectedJobTitle");
          const emailFromStorage = localStorage.getItem("email");
          if (selectedJobTitle && emailFromStorage) {
            await axios.post("https://raasbackend.vetrinahealthcare.com/jobtitle/", {
              id: idForSubmission,
              email: emailFromStorage,
              jobtitle: selectedJobTitle,
            });
          }
        } catch (e) {}

        navigate(returnPath);
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: response.data.message });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({ 
        icon: "error", 
        title: "Submission Error", 
        text: error.response?.data?.message || "Submission failed. Please try again." 
      });
    } finally {
      setIsLoading(false);
    }
  };

  function getCookie(name) {
    const cookieValue = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
    return cookieValue ? cookieValue.pop() : null;
  }

  // Calculate answered questions count
  const answeredQuestionsCount = questions.reduce((count, question, index) => {
    const isMCQ = isQuestionMCQ(question);
    const isTextAnswer = isQuestionTextAnswer(question);
    
    if (isMCQ && answers[index] !== null) {
      return count + 1;
    } else if (isTextAnswer && textAnswers[index] && textAnswers[index].trim() !== "") {
      return count + 1;
    }
    return count;
  }, 0);

  return (
    <div>
      <DashNavbar />
      <div className="wrapper">
        <Sidebar />
        <div className="container main personality" style={{ backgroundColor: "#ffffff", padding: "20px", borderRadius: "8px" }}>
          {isLoadingQuestions ? (
            <div className="text-center"><div className="spinner-border"></div><p>Loading...</p></div>
          ) : quizStarted ? (
            <div className="container auth-inner w-100" style={{ backgroundColor: "#ffffff", padding: "20px" }}>
              <h2 className="display-6 gradient-text mb-3">Personality Assessment</h2>
              
              {/* Progress indicator */}
              <div className="progress mb-4" style={{ height: "10px" }}>
                <div 
                  className="progress-bar bg-success" 
                  role="progressbar" 
                  style={{ width: `${(answeredQuestionsCount / questions.length) * 100}%` }}
                  aria-valuenow={answeredQuestionsCount}
                  aria-valuemin="0"
                  aria-valuemax={questions.length}
                ></div>
              </div>
              <div className="d-flex justify-content-between mb-4">
                <small className="text-muted">
                  Answered: {answeredQuestionsCount} of {questions.length} questions
                </small>
                <small className={`fw-bold ${answeredQuestionsCount === questions.length ? 'text-success' : 'text-warning'}`}>
                  {answeredQuestionsCount === questions.length ? 'All questions answered!' : 'Please answer all questions'}
                </small>
              </div>

              <div className="mb-1 row">
                <div className="col-md-12">
                  {questions.map((question, index) => (
                    <div 
                      key={question.id} 
                      id={`question-${index}`}
                      className={`mb-3 text-start p-3 ${validationErrors[index] ? 'border border-danger rounded bg-light' : ''}`}
                      style={{ 
                        backgroundColor: validationErrors[index] ? '#fff5f5' : 'transparent',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <div className="h5">
                        <hr />
                        <span className="text-primary">Question {index + 1}:</span> {question.question_text}
                        {validationErrors[index] && (
                          <span className="text-danger ms-2">
                            <i className="fas fa-exclamation-circle"></i> Required
                          </span>
                        )}
                      </div>
                      
                      {isQuestionTextAnswer(question) ? (
                        <>
                          <textarea 
                            className={`form-control ${validationErrors[index] ? 'is-invalid' : ''}`} 
                            rows="3" 
                            value={textAnswers[index] || ""} 
                            onChange={(e) => handleTextAnswerChange(index, e.target.value)}
                            placeholder="Please enter your answer here..."
                            required
                          />
                          {validationErrors[index] && (
                            <div className="invalid-feedback d-block">
                              Please provide an answer for this question
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="d-flex flex-column">
                          {question.answers.map((answer, answerIndex) => (
                            <div 
                              key={answerIndex}
                              className={`mb-2 p-2 rounded ${answers[index] === answerIndex ? 'border-primary border-2 bg-primary bg-opacity-10' : 'border border-gray-300'}`}
                              style={{ 
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                backgroundColor: answers[index] === answerIndex ? 'rgba(13, 110, 253, 0.1)' : 'transparent'
                              }}
                              onClick={() => handleResponseChange(index, answerIndex)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleResponseChange(index, answerIndex);
                                }
                              }}
                              tabIndex="0"
                              role="button"
                              aria-label={`Select option ${String.fromCharCode(65 + answerIndex)}: ${answer}`}
                            >
                              <div className="d-flex align-items-center">
                                <div 
                                  className={`rounded-circle border ${answers[index] === answerIndex ? 'border-primary bg-primary' : 'border-gray-400'}`}
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    minWidth: '20px',
                                    marginRight: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                  }}
                                >
                                  {answers[index] === answerIndex && (
                                    <div 
                                      className="rounded-circle bg-white"
                                      style={{
                                        width: '8px',
                                        height: '8px'
                                      }}
                                    ></div>
                                  )}
                                </div>
                                <div className="d-flex align-items-center">
                                  <span className="fw-bold me-2" style={{ minWidth: '20px' }}>
                                    {String.fromCharCode(65 + answerIndex)}.
                                  </span>
                                  <span>{answer}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                          {validationErrors[index] && touchedQuestions[index] && (
                            <div className="text-danger small mt-2">
                              <i className="fas fa-exclamation-circle me-1"></i>
                              Please select an option for this question
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <footer className="footer py-3 bg-light">
                <div className="container">
                  <div className="row">
                    <div className="col-6">
                      <button className="btn btn-secondary btn-lg fw-bold" onClick={() => navigate(returnPath)}>
                        Cancel
                      </button>
                    </div>
                    <div className="col-6 text-end">
                      <button 
                        className="btn btn-success btn-lg fw-bold" 
                        onClick={handleSubmit} 
                        disabled={isLoading || answeredQuestionsCount !== questions.length}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Submitting...
                          </>
                        ) : (
                          `Submit Quiz (${answeredQuestionsCount}/${questions.length})`
                        )}
                      </button>
                      {answeredQuestionsCount !== questions.length && (
                        <div className="text-warning small mt-2">
                          <i className="fas fa-info-circle me-1"></i>
                          Please answer all questions before submitting
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </footer>
            </div>
          ) : (
            <>
              <div className="container text-center mb-5">
                <img src={personality13} alt="Personality" className="imgpersonality" />
                <h1 className="display-4 fw-bold gradient-text mb-3">Personality Assessment</h1>
                <p className="lead mt-4">Assessments designed to measure traits, preferences, and behaviour patterns.</p>
                <div className="alert alert-info mt-4" role="alert">
                  <h4 className="alert-heading">Important Note:</h4>
                  <p>All questions are <strong>mandatory</strong>. You must answer every question before submitting the quiz.</p>
                  <hr />
                  <p className="mb-0">Total Questions: <strong>{questions.length}</strong></p>
                </div>
              </div>
              <footer className="footer py-3 bg-light mt-5">
                <div className="container">
                  <div className="row">
                    <div className="col-6">
                      <p className="mb-0">Questions: {questions.length}</p>
                    </div>
                    <div className="col-6 text-end">
                      <button 
                        className={`btn btn-primary fw-bold ${quizTaken ? "disabled" : ""}`} 
                        onClick={handleStartQuiz} 
                        disabled={quizTaken}
                      >
                        {quizTaken ? "Quiz Already Taken" : "Start Quiz"} <GoArrowRight />
                      </button>
                    </div>
                  </div>
                </div>
              </footer>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalityQuizForm;



// // PersonalityQuizForm.js
  // import { useState, useEffect } from "react";
  // import axios from "axios";
  // import DashNavbar from "./Misc/DashNavbar";
  // import Sidebar from "./Misc/Sidebar";
  // import { GoArrowRight } from "react-icons/go";
  // import personality13 from "../Assets/personality13.png";
  // import "../index.css";
  // import Swal from "sweetalert2";
  // // --- MODIFIED: Import useLocation ---
  // import { useNavigate, useLocation } from "react-router-dom";

  // const PersonalityQuizForm = () => {
  //   const [questions, setQuestions] = useState([]);
  //   const [answers, setAnswers] = useState([]);
  //   const [textAnswers, setTextAnswers] = useState([]);
  //   const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  //   const [quizStarted, setQuizStarted] = useState(false);
  //   const [quizTaken, setQuizTaken] = useState(false);
  //   const [isLoading, setIsLoading] = useState(false);
  //   const [jobDetails, setJobDetails] = useState(null); // This seems unused, but keeping it
  //     const [applicantId, setApplicantId] = useState(null);
    
  //   const navigate = useNavigate();
  //   // --- MODIFIED: Get location object which contains our state ---
  //   const location = useLocation();

  //   const jobId = localStorage.getItem("selected_job_id");
  //   const postingType = localStorage.getItem("selected_posting_type") || "Job_Opening";

  //   // --- MODIFIED: Determine the return path. Fallback to /job-openings if not provided. ---
  //   const returnPath = location.state?.from || "/job-openings";


  //   useEffect(() => {
  //     const user_id_from_storage = localStorage.getItem("user_id");

  //     if (!jobId) {
  //       Swal.fire({
  //         icon: "warning",
  //         title: "No Job Selected",
  //         text: "Please select a job before taking the quiz.",
  //         confirmButtonText: "Go Back to Jobs",
  //       }).then((result) => {
  //         if (result.isConfirmed) {
  //           // --- MODIFIED: Navigate to a valid default route ---
  //           navigate("/job-openings");
  //         }
  //       });
  //       return; // Exit the effect if jobId is missing
  //     }

  //     // This logic seems to check if a user has taken *any* quiz, not for a specific job.
  //     // Keeping as is, based on your provided code.
  //     const checkQuizStatus = async () => {
  //       try {
  //         const response = await axios.get(
  //           `https://raasbackend.vetrinahealthcare.com/check_quiz_status/${user_id_from_storage}/`
  //         );

  //         if ("quiz_taken" in response.data) {
  //           setQuizTaken(response.data.quiz_taken);
  //         }
  //       } catch (error) {
  //         console.error("Error checking quiz status:", error.message);
  //       }
  //     };

  //     const ensureApplicantExists = async () => {
  //       try {
  //         const resp = await axios.get(
  //           `https://raasbackend.vetrinahealthcare.com/applicants/data/?user_id=${user_id_from_storage}`
  //         );
  //         const data = resp?.data;
  //         const first = Array.isArray(data) ? data[0] : data;
  //         const exists = !!first;
  //         if (!exists) {
  //           await Swal.fire({
  //             icon: "warning",
  //             title: "Complete Your Profile",
  //             text: "We couldn't find your applicant profile. Please complete your profile before taking the quiz.",
  //             confirmButtonText: "Go to My Profile",
  //           });
  //           navigate("/jobapplication");
  //           return false;
  //         }
  //         // Try to extract applicant id from various common shapes
  //         const extractedId =
  //           (typeof first === "object" && first !== null && (first.id || first.applicant || first.pk)) ||
  //           (typeof first?.fields === "object" && (first.fields.id || first.fields.applicant)) ||
  //           null;
  //         if (extractedId) {
  //           setApplicantId(extractedId);
  //           localStorage.setItem("applicant_id", String(extractedId));
  //         }
  //         return true;
  //       } catch (e) {
  //         console.warn("Failed to verify applicant existence:", e?.message || e);
  //         return true; // allow flow; backend will still validate
  //       }
  //     };

  //     const fetchQuestions = async () => {
  //       setIsLoadingQuestions(true);
  //       try {
  //         // Try the new type-aware endpoint first, fall back to legacy endpoint for backward compatibility
  //         let response;
  //         try {
  //           response = await axios.get(
  //             `https://raasbackend.vetrinahealthcare.com/questions/${postingType}/${jobId}/`
  //           );
  //         } catch (e) {
  //           // Fallback to original endpoint if the above is not available
  //           response = await axios.get(
  //             `https://raasbackend.vetrinahealthcare.com/questions/${jobId}/`
  //           );
  //         }
  //         const data = response?.data;
  //         const normalized = Array.isArray(data)
  //           ? data
  //           : Array.isArray(data?.questions)
  //           ? data.questions
  //           : Array.isArray(data?.results)
  //           ? data.results
  //           : [];
  //         setQuestions(normalized);
  //         setAnswers(Array(normalized.length).fill(null));
  //         setTextAnswers(Array(normalized.length).fill(""));
  //       } catch (error) {
  //         console.error("Error fetching questions:", error);
  //         Swal.fire({
  //           icon: "error",
  //           title: "Failed to Load Quiz",
  //           text:
  //             "Could not load the personality quiz questions. Please try again later.",
  //         });
  //       } finally {
  //         setIsLoadingQuestions(false);
  //       }
  //     };

  //     const init = async () => {
  //       if (user_id_from_storage) {
  //         checkQuizStatus();
  //       }
  //       const ok = await ensureApplicantExists();
  //       if (ok) {
  //         fetchQuestions();
  //       }
  //     };

  //     init();
  //   }, [jobId, navigate]);

  //   const handleStartQuiz = () => {
  //     if (quizTaken) {
  //       Swal.fire({
  //         icon: "info",
  //         title: "Quiz Already Taken",
  //         text: "You have already completed the quiz for this job position.",
  //         timer: 4000,
  //         timerProgressBar: true,
  //         showConfirmButton: false,
  //       });
  //     } else {
  //       setQuizStarted(true);
  //     }
  //   };

  //   const handleResponseChange = (index, answerIndex) => {
  //     setAnswers((prevAnswers) => {
  //       const newAnswers = [...prevAnswers];
  //       newAnswers[index] = answerIndex;
  //       return newAnswers;
  //     });
  //   };

  //   const handleTextAnswerChange = (index, text) => {
  //     setTextAnswers((prevTextAnswers) => {
  //       const newTextAnswers = [...prevTextAnswers];
  //       newTextAnswers[index] = text;
  //       return newTextAnswers;
  //     });
  //   };

  //   const handleSubmit = async (event) => {
  //     setIsLoading(true);
  //     event.preventDefault();
  //     try {
  //       const user_id_from_storage = localStorage.getItem("user_id");
  //       if (!user_id_from_storage) {
  //         console.error("User ID not found in local storage.");
  //         setIsLoading(false);
  //         return;
  //       }

  //       // Build payloads per posting type
  //       const isJob = postingType === "Job_Opening";
  //       const isBusiness = postingType === "Business_Opportunity";
  //       const isConsultant = postingType === "Consultancy_Opportunity";

  //       const idForSubmissionRaw = applicantId || localStorage.getItem("applicant_id") || user_id_from_storage;
  //       const idForSubmission = Number.isNaN(parseInt(idForSubmissionRaw, 10))
  //         ? idForSubmissionRaw
  //         : parseInt(idForSubmissionRaw, 10);
  //       let payload = { applicant_id: idForSubmission, job_id: jobId };

  //       if (isJob || isBusiness) {
  //         // For job and business, server expects selected_answers: { Q1: "correct"|"wrong", ... }
  //         const selected_answers = {};
  //         (Array.isArray(questions) ? questions : []).forEach((question, idx) => {
  //           const userIndex = answers[idx];
  //           // Consider only objective questions with a correct index
  //           if (typeof question?.correct_answer_index === "number" && typeof userIndex === "number") {
  //             selected_answers[`Q${idx + 1}`] = userIndex === question.correct_answer_index ? "correct" : "wrong";
  //           } else {
  //             // For non-objective or unanswered, mark wrong to be conservative
  //             selected_answers[`Q${idx + 1}`] = "wrong";
  //           }
  //         });
  //         payload = {
  //           ...payload,
  //           quiz_type: isJob ? "job" : "business",
  //           selected_answers,
  //         };
  //       } else if (isConsultant) {
  //         // For consultant, server expects answers: [{ question_id, selected_answer_index }]
  //         const answersArray = (Array.isArray(questions) ? questions : []).map((q, idx) => ({
  //           question_id: q.id,
  //           selected_answer_index: typeof answers[idx] === "number" ? answers[idx] : null,
  //         }));
  //         payload = {
  //           ...payload,
  //           quiz_type: "consultant",
  //           answers: answersArray,
  //         };
  //       }

  //       const csrfToken = getCookie("csrftoken");

  //       const response = await axios.post(
  //         "https://raasbackend.vetrinahealthcare.com/save_quiz_answers/",
  //         payload,
  //         {
  //           headers: {
  //             "Content-Type": "application/json",
  //             "X-CSRFToken": csrfToken,
  //           },
  //         }
  //       );

  //       const success = response?.data?.success === true || typeof response?.data?.message === "string";
  //       if (success) {
  //         await Swal.fire({
  //           icon: "success",
  //           title: "Quiz Submitted Successfully!",
  //           text:
  //             "Thank you for completing the personality assessment for this job position.",
  //           timer: 2000,
  //           timerProgressBar: true,
  //           showConfirmButton: false,
  //         });
          
  //         // After a successful quiz submission, register this candidate under Applicants for the selected posting
  //         try {
  //           const selectedJobTitle = localStorage.getItem("selectedJobTitle");
  //           const emailFromStorage = localStorage.getItem("email");
  //           const applicantIdToRegister = idForSubmission;
  //           if (selectedJobTitle && emailFromStorage && applicantIdToRegister) {
  //             await axios.post(
  //               "https://raasbackend.vetrinahealthcare.com/jobtitle/",
  //               {
  //                 id: applicantIdToRegister,
  //                 email: emailFromStorage,
  //                 jobtitle: selectedJobTitle,
  //               }
  //             );
  //           }
  //         } catch (e) {
  //           // Non-blocking: even if this fails, the quiz submission was successful
  //           console.warn("Failed to register applicant after quiz submission:", e?.message || e);
  //         }

  //         // --- MODIFIED: Navigate to the stored return path ---
  //         navigate(returnPath);

  //       } else if ("error" in (response?.data || {})) {
  //         Swal.fire({
  //           icon: "error",
  //           title: "Quiz Submission Failed",
  //           text: response.data.error,
  //         });
  //       }
  //     } catch (error) {
  //       console.error("Error:", error.message);
  //       Swal.fire({
  //         icon: "error",
  //         title: "Quiz Submission Failed",
  //         text: "An error occurred while submitting the quiz. Please try again.",
  //       });
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  //   function getCookie(name) {
  //     const cookieValue = document.cookie.match(
  //       "(^|;)\\s*" + name + "\\s*=\\s*([^;]+)"
  //     );
  //     return cookieValue ? cookieValue.pop() : null;
  //   }

  //   return (
  //     <div>
  //       <div>
  //         <DashNavbar />
  //         <div className="wrapper">
  //           <Sidebar />
  //           <div
  //             className="container main personality"
  //             style={{
  //               backgroundColor: "#ffffff",
  //               padding: "20px",
  //               borderRadius: "8px",
  //             }}
  //           >
  //             {isLoadingQuestions ? (
  //               <div className="text-center">
  //                 <div className="spinner-border" role="status">
  //                   <span className="visually-hidden">Loading...</span>
  //                 </div>
  //                 <p>Loading questions...</p>
  //               </div>
  //             ) : quizStarted ? (
  //               <>
  //                 <div
  //                   className="container auth-inner w-100 "
  //                   style={{
  //                     backgroundColor: "#ffffff",
  //                     padding: "20px",
  //                     borderRadius: "8px",
  //                   }}
  //                 >
  //                   <h2 className="display-6 gradient-text mb-3">
  //                     Personality Assessment
  //                   </h2>
  //                   <div className="mb-1 row">
  //                     <div className="col-md-12">
  //                       {(Array.isArray(questions) ? questions : []).map((question, index) => (
  //                         <div key={question.id} className="mb-3 text-start">
  //                           <p className="h5" text-start>
  //                             <hr />
  //                             {`Question ${index + 1}: ${question.question_text}`}
  //                           </p>

  //                           {question.correct_answer_index === null &&
  //                           question.answers.length === 1 &&
  //                           question.answers[0] === "" ? (
  //                             <div className="mb-3">
  //                               <textarea
  //                                 className="form-control"
  //                                 rows="3"
  //                                 placeholder="Your Answer"
  //                                 value={textAnswers[index]}
  //                                 onChange={(e) =>
  //                                   handleTextAnswerChange(index, e.target.value)
  //                                 }
  //                               />
  //                             </div>
  //                           ) : (
  //                             <div className="d-flex flex-column">
  //                               {question.answers.map((answer, answerIndex) => (
  //                                 <label
  //                                   key={answerIndex}
  //                                   className={`btn ${
  //                                     answers[index] === answerIndex
  //                                       ? "btn-primary"
  //                                       : "btn-outline-primary"
  //                                   } mb-2 text-start`}
  //                                 >
  //                                   <input
  //                                     type="radio"
  //                                     style={{ display: "none" }}
  //                                     value={answerIndex}
  //                                     checked={answers[index] === answerIndex}
  //                                     onChange={() =>
  //                                       handleResponseChange(index, answerIndex)
  //                                     }
  //                                   />
  //                                   {answer}
  //                                 </label>
  //                               ))}
  //                             </div>
  //                           )}
  //                         </div>
  //                       ))}
  //                     </div>
  //                   </div>
  //                   <footer className="footer py-3 bg-light">
  //                     <div className="container">
  //                       <div className="row">
  //                         <div className="col-6 text-start">
  //                           <button
  //                             className="btn btn-secondary btn-lg fw-bold"
  //                             // --- MODIFIED: Use returnPath for cancel button ---
  //                             onClick={() => navigate(returnPath)}
  //                             disabled={isLoading}
  //                           >
  //                             Cancel
  //                           </button>
  //                         </div>
  //                         <div className="col-6 text-end">
  //                           <button
  //                             className="btn btn-success btn-lg fw-bold"
  //                             onClick={handleSubmit}
  //                             disabled={isLoading}
  //                           >
  //                             {isLoading ? (
  //                               <>
  //                                 <span
  //                                   className="spinner-border spinner-border-sm me-2"
  //                                   role="status"
  //                                   aria-hidden="true"
  //                                 ></span>
  //                                 Submitting...
  //                               </>
  //                             ) : (
  //                               "Submit Quiz"
  //                             )}
  //                           </button>
  //                         </div>
  //                       </div>
  //                     </div>
  //                   </footer>
  //                 </div>
  //               </>
  //             ) : (
  //               <>
  //                 <div className="container text-center mb-5">
  //                   <img
  //                     src={personality13 || "/placeholder.svg"}
  //                     alt="Admin"
  //                     className="imgpersonality"
  //                   />
  //                   <h1 className="display-4 fw-bold gradient-text mb-3">
  //                     Personality Assessment
  //                   </h1>
  //                   <p className="lead mt-4">
  //                     Personality assessments are tools designed to measure and
  //                     evaluate various aspects of an individual's personality.
  //                     These assessments aim to provide insights into an
  //                     individual's traits, preferences, behaviour patterns, and
  //                     emotional tendencies.
  //                   </p>
  //                   <h2 className="text-dark mt-5 mb-5 ">
  //                     Ready to start the quiz?
  //                   </h2>
  //                 </div>

  //                 <footer className="footer py-3 bg-light mt-5">
  //                   <div className="container ">
  //                     <div className="row">
  //                       <div className="col-6">
  //                         <p className="">
  //                           The Personality Assessment | {(Array.isArray(questions) ? questions : []).length} Questions
  //                         </p>
  //                       </div>
  //                       <div className="col-6 text-end">
  //                         <button
  //                           className={`btn btn-primary fw-bold ${
  //                             quizTaken ? "disabled" : ""
  //                           }`}
  //                           onClick={handleStartQuiz}
  //                           disabled={quizTaken}
  //                         >
  //                           {quizTaken ? "Quiz Already Taken" : "Start Quiz"}{" "}
  //                           <GoArrowRight />
  //                         </button>
  //                       </div>
  //                     </div>
  //                   </div>
  //                 </footer>
  //               </>
  //             )}
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  // export default PersonalityQuizForm;


// // PersonalityQuizForm.js
// import { useState, useEffect } from "react";
// import axios from "axios";
// import DashNavbar from "./Misc/DashNavbar";
// import Sidebar from "./Misc/Sidebar";
// import { GoArrowRight } from "react-icons/go";
// import personality13 from "../Assets/personality13.png";
// import "../index.css";
// import Swal from "sweetalert2";
// import { useNavigate } from "react-router-dom";

// const PersonalityQuizForm = () => {
//   const [questions, setQuestions] = useState([]);
//   const [answers, setAnswers] = useState([]);
//   const [textAnswers, setTextAnswers] = useState([]);
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
//   const [quizStarted, setQuizStarted] = useState(false);
//   const [quizTaken, setQuizTaken] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const [jobDetails, setJobDetails] = useState(null);
//   const navigate = useNavigate();

//   const jobId = localStorage.getItem("selected_job_id");

//   useEffect(() => {
//     const user_id_from_storage = localStorage.getItem("user_id");

//     if (!jobId) {
//       Swal.fire({
//         icon: "warning",
//         title: "No Job Selected",
//         text: "Please select a job before taking the quiz.",
//         confirmButtonText: "Go Back to Jobs",
//       }).then((result) => {
//         if (result.isConfirmed) {
//           navigate("/Jobpostingresult");
//         }
//       });
//       return; // Exit the effect if jobId is missing
//     }

//     // const fetchJobDetails = async () => {
//     //   try {
//     //     const response = await axios.get(
//     //       `https://raasbackend.vetrinahealthcare.com/job_details/${jobId}/`
//     //     );
//     //     setJobDetails(response.data);
//     //   } catch (error) {
//     //     console.error("Error fetching job details:", error);
//     //   }
//     // };

//     const checkQuizStatus = async () => {
//       try {
//         const response = await axios.get(
//           `https://raasbackend.vetrinahealthcare.com/check_quiz_status/${user_id_from_storage}/`
//         );

//         if ("quiz_taken" in response.data) {
//           setQuizTaken(response.data.quiz_taken);
//         }
//       } catch (error) {
//         console.error("Error checking quiz status:", error.message);
//       }
//     };

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       try {
//         const response = await axios.get(
//           `https://raasbackend.vetrinahealthcare.com/questions/${jobId}/`
//         );
//         setQuestions(response.data);
//         setAnswers(Array(response.data.length).fill(null));
//         setTextAnswers(Array(response.data.length).fill(""));
//       } catch (error) {
//         console.error("Error fetching questions:", error);
//         Swal.fire({
//           icon: "error",
//           title: "Failed to Load Quiz",
//           text:
//             "Could not load the personality quiz questions. Please try again later.",
//         });
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };

//     if (user_id_from_storage) {
//       checkQuizStatus();
//     }

//     // fetchJobDetails();
//     fetchQuestions();
//   }, [jobId, navigate]);

//   const handleStartQuiz = () => {
//     if (quizTaken) {
//       Swal.fire({
//         icon: "info",
//         title: "Quiz Already Taken",
//         text: "You have already completed the quiz for this job position.",
//         timer: 4000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     } else {
//       setQuizStarted(true);
//     }
//   };

//   const handleResponseChange = (index, answerIndex) => {
//     setAnswers((prevAnswers) => {
//       const newAnswers = [...prevAnswers];
//       newAnswers[index] = answerIndex;
//       return newAnswers;
//     });
//   };

//   const handleTextAnswerChange = (index, text) => {
//     setTextAnswers((prevTextAnswers) => {
//       const newTextAnswers = [...prevTextAnswers];
//       newTextAnswers[index] = text;
//       return newTextAnswers;
//     });
//   };

//   const handleSubmit = async (event) => {
//     setIsLoading(true);
//     event.preventDefault();
//     try {
//       const user_id_from_storage = localStorage.getItem("user_id");
//       if (!user_id_from_storage) {
//         console.error("User ID not found in local storage.");
//         return;
//       }

//       const formattedAnswers = questions.map((question, questionIndex) => {
//         if (
//           question.correct_answer_index === null &&
//           question.answers.length === 1 &&
//           question.answers[0] === ""
//         ) {
//           return {
//             question_id: question.id,
//             selected_answer_text: textAnswers[questionIndex],
//           };
//         } else if (answers[questionIndex] === null) {
//           return {
//             question_id: question.id,
//             selected_answer_text: "",
//           };
//         } else {
//           return {
//             question_id: question.id,
//             selected_answer_index: answers[questionIndex],
//           };
//         }
//       });

//       const csrfToken = getCookie("csrftoken");

//       const response = await axios.post(
//         "https://raasbackend.vetrinahealthcare.com/save_quiz_answers/",
//         {
//           user_id: user_id_from_storage,
//           job_id: jobId,
//           answers: formattedAnswers,
//         },
//         {
//           headers: {
//             "Content-Type": "application/json",
//             "X-CSRFToken": csrfToken,
//           },
//         }
//       );

//       if ("message" in response.data) {
//         await Swal.fire({
//           icon: "success",
//           title: "Quiz Submitted Successfully!",
//           text:
//             "Thank you for completing the personality assessment for this job position.",
//           timer: 4000,
//           timerProgressBar: true,
//           showConfirmButton: false,
//         });

//         const appliedJobs = JSON.parse(
//           localStorage.getItem("appliedJobs") || "{}"
//         );
//         if (jobDetails && jobDetails.id) {
//           appliedJobs[jobDetails.id] = "Applied";
//           localStorage.setItem("appliedJobs", JSON.stringify(appliedJobs));
//         }

//         navigate("/Jobpostingresult");
//       } else if ("error" in response.data) {
//         Swal.fire({
//           icon: "error",
//           title: "Quiz Submission Failed",
//           text: response.data.error,
//           timer: 4000,
//           timerProgressBar: true,
//           showConfirmButton: false,
//         });
//       }
//     } catch (error) {
//       console.error("Error:", error.message);
//       Swal.fire({
//         icon: "error",
//         title: "Quiz Submission Failed",
//         text: "An error occurred while submitting the quiz. Please try again.",
//         timer: 4000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   function getCookie(name) {
//     const cookieValue = document.cookie.match(
//       "(^|;)\\s*" + name + "\\s*=\\s*([^;]+)"
//     );
//     return cookieValue ? cookieValue.pop() : null;
//   }

//   return (
//     <div>
//       <div>
//         <DashNavbar />
//         <div className="wrapper">
//           <Sidebar />
//           <div
//             className="container main personality"
//             style={{
//               backgroundColor: "#ffffff",
//               padding: "20px",
//               borderRadius: "8px",
//             }}
//           >
//             {isLoadingQuestions ? (
//               <div className="text-center">
//                 <div className="spinner-border" role="status">
//                   <span className="visually-hidden">Loading...</span>
//                 </div>
//                 <p>Loading questions...</p>
//               </div>
//             ) : quizStarted ? (
//               <>
//                 <div
//                   className="container auth-inner w-100 "
//                   style={{
//                     backgroundColor: "#ffffff",
//                     padding: "20px",
//                     borderRadius: "8px",
//                   }}
//                 >
//                   <h2 className="display-6 gradient-text mb-3">
//                     Personality Assessment
//                   </h2>

//                   {jobDetails && (
//                     <div className="mb-4 p-3 bg-light rounded">
//                       <h5>{jobDetails.jobtitle}</h5>
//                       <p className="mb-1">
//                         <strong>Job ID:</strong> {jobDetails.job_id}
//                       </p>
//                       {jobDetails.job_location && (
//                         <p className="mb-1">
//                           <strong>Location:</strong> {jobDetails.job_location}
//                         </p>
//                       )}
//                       {jobDetails.role_type && (
//                         <p className="mb-0">
//                           <strong>Role Type:</strong> {jobDetails.role_type}
//                         </p>
//                       )}
//                     </div>
//                   )}

//                   <div className="mb-1 row">
//                     <div className="col-md-12">
//                       {questions.map((question, index) => (
//                         <div key={question.id} className="mb-3 text-start">
//                           <p className="h5" text-start>
//                             <hr />
//                             {`Question ${index + 1}: ${question.question_text}`}
//                           </p>

//                           {question.correct_answer_index === null &&
//                           question.answers.length === 1 &&
//                           question.answers[0] === "" ? (
//                             <div className="mb-3">
//                               <textarea
//                                 className="form-control"
//                                 rows="3"
//                                 placeholder="Your Answer"
//                                 value={textAnswers[index]}
//                                 onChange={(e) =>
//                                   handleTextAnswerChange(index, e.target.value)
//                                 }
//                               />
//                             </div>
//                           ) : (
//                             <div className="d-flex flex-column">
//                               {question.answers.map((answer, answerIndex) => (
//                                 <label
//                                   key={answerIndex}
//                                   className={`btn ${
//                                     answers[index] === answerIndex
//                                       ? "btn-primary"
//                                       : "btn-outline-primary"
//                                   } mb-2 text-start`}
//                                 >
//                                   <input
//                                     type="radio"
//                                     style={{ display: "none" }}
//                                     value={answerIndex}
//                                     checked={answers[index] === answerIndex}
//                                     onChange={() =>
//                                       handleResponseChange(index, answerIndex)
//                                     }
//                                   />
//                                   {answer}
//                                 </label>
//                               ))}
//                             </div>
//                           )}
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                   <footer className="footer py-3 bg-light">
//                     <div className="container">
//                       <div className="row">
//                         <div className="col-6 text-start">
//                           <button
//                             className="btn btn-secondary btn-lg fw-bold"
//                             onClick={() => navigate("/Jobpostingresult")}
//                             disabled={isLoading}
//                           >
//                             Cancel
//                           </button>
//                         </div>
//                         <div className="col-6 text-end">
//                           <button
//                             className="btn btn-success btn-lg fw-bold"
//                             onClick={handleSubmit}
//                             disabled={isLoading}
//                           >
//                             {isLoading ? (
//                               <>
//                                 <span
//                                   className="spinner-border spinner-border-sm me-2"
//                                   role="status"
//                                   aria-hidden="true"
//                                 ></span>
//                                 Submitting...
//                               </>
//                             ) : (
//                               "Submit Quiz"
//                             )}
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   </footer>
//                 </div>
//               </>
//             ) : (
//               <>
//                 <div className="container text-center mb-5">
//                   <img
//                     src={personality13 || "/placeholder.svg"}
//                     alt="Admin"
//                     className="imgpersonality"
//                   />
//                   <h1 className="display-4 fw-bold gradient-text mb-3">
//                     Personality Assessment
//                   </h1>

//                   {jobDetails && (
//                     <div className="mb-4 p-3 bg-light rounded">
//                       <h5>{jobDetails.jobtitle}</h5>
//                       <p className="mb-1">
//                         <strong>Job ID:</strong> {jobDetails.job_id}
//                       </p>
//                       {jobDetails.job_location && (
//                         <p className="mb-1">
//                           <strong>Location:</strong> {jobDetails.job_location}
//                         </p>
//                       )}
//                       {jobDetails.role_type && (
//                         <p className="mb-0">
//                           <strong>Role Type:</strong> {jobDetails.role_type}
//                         </p>
//                       )}
//                     </div>
//                   )}

//                   <p className="lead mt-4">
//                     Personality assessments are tools designed to measure and
//                     evaluate various aspects of an individual's personality.
//                     These assessments aim to provide insights into an
//                     individual's traits, preferences, behaviour patterns, and
//                     emotional tendencies.
//                   </p>
//                   <h2 className="text-dark mt-5 mb-5 ">
//                     Ready to start the quiz?
//                   </h2>
//                 </div>

//                 <footer className="footer py-3 bg-light mt-5">
//                   <div className="container ">
//                     <div className="row">
//                       <div className="col-6">
//                         <p className="">
//                           The Personality Assessment | {questions.length} Questions
//                         </p>
//                       </div>
//                       <div className="col-6 text-end">
//                         <button
//                           className={`btn btn-primary fw-bold ${
//                             quizTaken ? "disabled" : ""
//                           }`}
//                           onClick={handleStartQuiz}
//                           disabled={quizTaken}
//                         >
//                           {quizTaken ? "Quiz Already Taken" : "Start Quiz"}{" "}
//                           <GoArrowRight />
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 </footer>
//               </>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default PersonalityQuizForm;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import DashNavbar from "./Misc/DashNavbar";
// import Sidebar from "./Misc/Sidebar";
// import { GoArrowRight } from "react-icons/go";
// import personality13 from "../Assets/personality13.png";
// import "../index.css";
// import Swal from "sweetalert2";

// const PersonalityQuizForm = () => {
//   const traitOrder = [
//     "extraversion",
//     "agreeableness",
//     "openness",
//     "conscientiousness",
//     "neuroticism",
//   ];

//   const [answers, setAnswers] = useState({
//     extraversion: Array.from({ length: 5 }, () => null),
//     agreeableness: Array.from({ length: 5 }, () => null),
//     openness: Array.from({ length: 5 }, () => null),
//     conscientiousness: Array.from({ length: 5 }, () => null),
//     neuroticism: Array.from({ length: 5 }, () => null),
//   });

//   const questions = {
//     extraversion: [
//       "I enjoy social gatherings and meeting new people.",
//       "I prefer spending time alone rather than with a large group.",
//       "I am outgoing and talkative.",
//       "I am reserved and quiet.",
//       "I enjoy being the center of attention.",
//     ],
//     agreeableness: [
//       "I am generally compassionate and empathetic.",
//       "I prioritize getting my work done over helping others.",
//       "I avoid conflicts and arguments.",
//       "I am willing to stand up for what I believe, even if it causes conflict.",
//       "I am easygoing and forgiving.",
//     ],
//     openness: [
//       "I am curious about the world and enjoy trying new things.",
//       "I prefer routine and familiarity over novelty.",
//       "I am imaginative and creative.",
//       "I stick to traditional ways of doing things.",
//       "I am open-minded and embrace new ideas.",
//     ],
//     conscientiousness: [
//       "I am organized and like to have a plan for everything.",
//       "I am spontaneous and go with the flow.",
//       "I am focused on achieving my goals.",
//       "I often procrastinate and leave things until the last minute.",
//       "I pay attention to details and strive for perfection.",
//     ],
//     neuroticism: [
//       "I am generally calm and emotionally stable.",
//       "I tend to worry a lot about the future.",
//       "I am easily stressed and anxious.",
//       "I rarely feel sad or depressed.",
//       "I am emotionally sensitive and easily get upset.",
//     ],
//   };

//   const responseOptions = [
//     "Strongly Disagree",
//     "Disagree",
//     "Neutral",
//     "Agree",
//     "Strongly Agree",
//   ];

//   const mapResponseToInt = (response) => {
//     switch (response) {
//       case "Strongly Disagree":
//         return 1;
//       case "Disagree":
//         return 2;
//       case "Neutral":
//         return 3;
//       case "Agree":
//         return 4;
//       case "Strongly Agree":
//         return 5;
//       default:
//         return 3;
//     }
//   };

//   const [quizStarted, setQuizStarted] = useState(false);
//   const [quizTaken, setQuizTaken] = useState(false);

//   useEffect(() => {
//     const user_id_from_storage = localStorage.getItem("user_id");
//     const checkQuizStatus = async () => {
//       try {
//         const response = await axios.get(`https://raasbackend.vetrinahealthcare.com/check_quiz_status/${user_id_from_storage}/`
//         );

//         if ("quiz_taken" in response.data) {
//           setQuizTaken(response.data.quiz_taken);
//         }
//       } catch (error) {
//         console.error("Error checking quiz status:", error.message);
//       }
//     };

//     if (user_id_from_storage) {
//       checkQuizStatus();
//     }
//   }, []);

//   const handleStartQuiz = () => {
//     if (quizTaken) {
//       Swal.fire({
//         icon: "info",
//         title: "Quiz Already Taken",
//         text: "You have already completed the quiz.",
//         timer: 4000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     } else {
//       setQuizStarted(true);
//     }
//   };

//   const handleResponseChange = (trait, index, value) => {
//     setAnswers((prevAnswers) => ({
//       ...prevAnswers,
//       [trait]: prevAnswers[trait].map((prevValue, i) =>
//         i === index ? value : prevValue
//       ),
//     }));
//   };
//   const areAllQuestionsAnswered = () => {
//     for (const trait of traitOrder) {
//       for (const answer of answers[trait]) {
//         if (
//           answer === null ||
//           answer === undefined ||
//           answer === "" ||
//           answer === 0
//         ) {
//           return false;
//         }
//       }
//     }
//     return true;
//   };
//   const [isLoading, setIsLoading] = useState(false);

//   const handleSubmit = async (event) => {
//     setIsLoading(true); // Start loading
//     event.preventDefault();
//     try {
//       const user_id_from_storage = localStorage.getItem("user_id");
//       if (!user_id_from_storage) {
//         console.error("User ID not found in local storage.");
//         return;
//       }

//       if (!areAllQuestionsAnswered()) {
//         Swal.fire({
//           icon: "warning",
//           title: "Incomplete Quiz",
//           text: "Please answer all questions before submitting the quiz.",
//           timer: 4000,
//           timerProgressBar: true,
//           showConfirmButton: false,
//         });
//         return;
//       }

//       const formattedAnswers = {};
//       traitOrder.forEach((trait) => {
//         formattedAnswers[trait] = answers[trait].map((response) =>
//           mapResponseToInt(response)
//         );
//       });

//       const csrfToken = getCookie("csrftoken");

//       const response = await axios.post(
//         "https://raasbackend.vetrinahealthcare.com/save_personality_result/",
//         { user_id: user_id_from_storage, answers: formattedAnswers },
//         {
//           headers: {
//             "Content-Type": "application/json",
//             "X-CSRFToken": csrfToken,
//           },
//         }
//       );

//       if ("message" in response.data) {
//         await Swal.fire({
//           icon: "success",
//           title: "Quiz Submitted Successfully!",
//           text: "Thank you for completing the personality assessment.",
//           timer: 4000,
//           timerProgressBar: true,
//           showConfirmButton: false,
//         });
//         window.location.reload();
//       } else if ("error" in response.data) {
//         Swal.fire({
//           icon: "error",
//           title: "Quiz Submission Failed",
//           text: response.data.error,
//           timer: 4000,
//           timerProgressBar: true,
//           showConfirmButton: false,
//         });
//       }
//     } catch (error) {
//       console.error("Error:", error.message);
//       Swal.fire({
//         icon: "error",
//         title: "Quiz Submission Failed",
//         text: "An error occurred while submitting the quiz. Please try again.",
//         timer: 4000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     } finally {
//       // Stop the loading spinner
//       setIsLoading(false);
//     }
//   };


//   function getButtonClass(response) {
//     switch (response) {
//       case "Strongly Agree":
//         return "btn-dark-green";
//       case "Agree":
//         return "btn-faint-green";
//       case "Disagree":
//         return "btn-faint-violet";
//       case "Strongly Disagree":
//         return "btn-dark-violet";
//       default:
//         return "btn-ashgray";
//     }
//   }

//   function getCookie(name) {
//     const cookieValue = document.cookie.match(
//       "(^|;)\\s*" + name + "\\s*=\\s*([^;]+)"
//     );
//     return cookieValue ? cookieValue.pop() : null;
//   }

//   return (
//     <div>
//       <div

//       >
//         <DashNavbar />
//         <div className="wrapper">
//           {" "}
//           <Sidebar />
//           <div
//             className="container main personality"
//             style={{
//               backgroundColor: "#ffffff",
//               padding: "20px",
//               borderRadius: "8px",
//             }}

//           >
//             {quizStarted ? (
//               <>
//                 <div
//                   className="container auth-inner w-100 "
//                   style={{
//                     backgroundColor: "#ffffff",
//                     padding: "20px",
//                     borderRadius: "8px",
//                   }}
//                 >
//                   <h2 class="display-6  gradient-text mb-3">
//                     Personality Assessment
//                   </h2>

//                   <div className="mb-1 row">
//                     <div className="col-md-12">
//                       {traitOrder.map((trait) => (
//                         <div key={trait}>
//                           {answers[trait].map((answer, index) => (
//                             <div key={index} className="mb-3 text-start">
//                               <p className="h5">
//                                 <hr />
//                                 {`Question ${index + 1 + traitOrder.indexOf(trait) * 5
//                                   }: ${questions[trait][index]}`}
//                               </p>
//                               <div className="d-flex align-items-start personality2">
//                                 {responseOptions.map((option, optionIndex) => (
//                                   <div
//                                     key={optionIndex}
//                                     className="d-flex align-items-start mb-2"
//                                   >
//                                     <label
//                                       className={`btn ${answers[trait][index] === option
//                                         ? getButtonClass(option)
//                                         : "btn-outline-none"
//                                         }`}
//                                       style={{ margin: "25px 30px 25px 25px" }}
//                                     >
//                                       <input
//                                         type="radio"
//                                         style={{ display: "none" }}
//                                         value={option}
//                                         checked={
//                                           answers[trait][index] === option
//                                         }
//                                         onChange={() =>
//                                           handleResponseChange(
//                                             trait,
//                                             index,
//                                             option
//                                           )
//                                         }
//                                       />
//                                       {option}
//                                     </label>
//                                   </div>
//                                 ))}
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                   <footer className="footer py-3 bg-light">
//                     <div className="container">
//                       <button
//                         className="btn btn-success btn-lg fw-bold"
//                         onClick={handleSubmit}
//                         disabled={isLoading} // Disable button while loading
//                       >

//                         {isLoading ? (
//                           <span
//                             className="spinner-border spinner-border-sm"
//                             role="status"
//                             aria-hidden="true"
//                           ></span>
//                         ) : (
//                           " Submit Quiz"
//                         )}

//                       </button>
//                     </div>
//                   </footer>
//                 </div>
//               </>
//             ) : (
//               <>
//                 <div className="container text-center mb-5">
//                   <img
//                     src={personality13}
//                     alt="Admin"
//                     className="imgpersonality"
//                   />
//                   <h1 className="display-4 fw-bold gradient-text mb-3">
//                     Personality Assessment
//                   </h1>
//                   <p className="lead mt-4">
//                     Personality assessments are tools designed to measure and
//                     evaluate various aspects of an individual's personality.
//                     These assessments aim to provide insights into an
//                     individual's traits, preferences, behaviour patterns, and
//                     emotional tendencies.
//                   </p>
//                   <h2 className="text-dark mt-5 mb-5 ">
//                     Ready to start the quiz?
//                   </h2>
//                 </div>

//                 <footer className="footer py-3 bg-light mt-5">
//                   <div className="container ">
//                     <div className="row">
//                       <div className="col-6">
//                         <p className="">
//                           The Personality Assessment | 25 Questions
//                         </p>
//                       </div>
//                       <div className="col-6 text-end">
//                         <button
//                           className={`btn btn-primary fw-bold ${quizTaken ? "disabled" : ""
//                             }`}
//                           onClick={handleStartQuiz}
//                           disabled={quizTaken}
//                         >
//                           {quizTaken ? "Quiz Already Taken" : "Start Quiz"}{" "}
//                           <GoArrowRight />
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 </footer>
//               </>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default PersonalityQuizForm;




